import React from 'react'
import { CheckCircle, XCircle, Clock, AlertTriangle, Loader2 } from 'lucide-react'
import clsx from 'clsx'

const TaskCard = ({ task }) => {
  const getStatusIcon = () => {
    switch (task.status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-success-500" />
      case 'failed':
        return <XCircle className="w-4 h-4 text-error-500" />
      case 'processing':
        return <Loader2 className="w-4 h-4 text-warning-500 animate-spin" />
      case 'pending':
        return <Clock className="w-4 h-4 text-gray-400" />
      default:
        return <AlertTriangle className="w-4 h-4 text-gray-400" />
    }
  }

  const getStatusColor = () => {
    switch (task.status) {
      case 'completed': return 'border-success-200 bg-success-50'
      case 'failed': return 'border-error-200 bg-error-50'
      case 'processing': return 'border-warning-200 bg-warning-50'
      case 'pending': return 'border-gray-200 bg-gray-50'
      default: return 'border-gray-200 bg-gray-50'
    }
  }

  const formatTime = (timestamp) => {
    if (!timestamp) return ''
    return new Date(timestamp).toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit' 
    })
  }

  const getProgressPercentage = () => {
    if (!task.steps || task.steps.length === 0) return 0
    const completedSteps = task.steps.filter(step => step.status === 'completed').length
    return (completedSteps / task.steps.length) * 100
  }

  return (
    <div className={clsx(
      'p-3 rounded-lg border transition-all hover:shadow-sm',
      getStatusColor()
    )}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center space-x-2">
          {getStatusIcon()}
          <span className="text-sm font-medium capitalize">
            {task.status}
          </span>
        </div>
        <div className="text-xs text-gray-500">
          {formatTime(task.timestamp)}
        </div>
      </div>

      <div className="text-xs text-gray-600 mb-2">
        Task ID: {task.task_id.slice(-12)}
      </div>

      {task.message && (
        <div className="text-sm text-gray-700 mb-2">
          {task.message}
        </div>
      )}

      {task.error && (
        <div className="text-sm text-error-600 mb-2 flex items-start space-x-1">
          <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
          <span className="truncate">{task.error}</span>
        </div>
      )}

      {/* Progress Bar */}
      {task.status === 'processing' && task.steps && task.steps.length > 0 && (
        <div className="mb-2">
          <div className="flex justify-between text-xs text-gray-500 mb-1">
            <span>Progress</span>
            <span>{Math.round(getProgressPercentage())}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-1.5">
            <div 
              className="bg-warning-400 h-1.5 rounded-full transition-all duration-300"
              style={{ width: `${getProgressPercentage()}%` }}
            />
          </div>
        </div>
      )}

      {/* Current Step */}
      {task.steps && task.steps.length > 0 && (
        <div className="text-xs">
          <div className="text-gray-500 mb-1">Current Step:</div>
          <div className="flex items-center space-x-1">
            <div className={clsx(
              'w-1.5 h-1.5 rounded-full',
              task.steps[task.steps.length - 1]?.status === 'completed' ? 'bg-success-400' :
              task.steps[task.steps.length - 1]?.status === 'processing' ? 'bg-warning-400 animate-pulse' :
              task.steps[task.steps.length - 1]?.status === 'failed' ? 'bg-error-400' : 'bg-gray-300'
            )} />
            <span className="text-gray-700 truncate">
              {task.steps[task.steps.length - 1]?.name || 'Initializing...'}
            </span>
          </div>
        </div>
      )}

      {/* Results Summary */}
      {task.status === 'completed' && task.result && (
        <div className="mt-2 text-xs">
          <div className="text-gray-500 mb-1">Results:</div>
          <div className="space-y-1">
            {task.result.places_found && (
              <div className="flex justify-between">
                <span>Places found:</span>
                <span className="font-medium">{task.result.places_found}</span>
              </div>
            )}
            {task.result.successful_crawls !== undefined && (
              <div className="flex justify-between">
                <span>Successful crawls:</span>
                <span className="font-medium">{task.result.successful_crawls}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default TaskCard